import React, { useCallback, useEffect, useState } from "react";
import {
  App,
  Button,
  Divider,
  Popconfirm,
  Steps,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import GenericCard from "components/UI/GenericCard/GenericCard";
import LinkTelegramAccount from "components/UserSettings/LinkTelegramAccount";
import UpdateIBKRDetails from "components/UserSettings/UpdateIBKRDetails";
import AutomationLogsModal from "components/UserSettings/AutomationLogsModal";
import { IBAccountSection } from "components/IBIntergration/IBIntergration";
import {
  apiGetAutomationOverview,
  apiGetIBAccountDetails,
  apiGetTwsStatus,
  apiRestartTws,
  apiStartAutomation,
  apiStartTws,
  apiStopAutomation,
  apiStopTws,
} from "api/apiTws";
import "./Automation.scss";

dayjs.extend(relativeTime);

const REFRESH_MS = 15000;
const BOT_REFRESH_MS = 30000; // asks the bot itself (socket): gateway state, account
const STATS_FRESH_MS = 2 * 60 * 1000; // the bot sends stats every ~30s
// the trading loop checks in every few seconds: silent longer while the
// container answers = the loop is stuck (the Telegram alert waits 2 minutes)
const LOOP_SILENT_SECONDS = 30;

const ago = (date) => (date ? dayjs(date).fromNow() : "-");
const ms = (value) =>
  value == null
    ? "-"
    : value >= 1000
      ? `${(value / 1000).toFixed(1)}s`
      : `${value}ms`;

// Everything about the user's AutoTrader: the setup a new user goes through
// (Telegram -> AutoTrader program -> IBKR login -> IB Gateway -> automation on),
// its health (stats the bot sends every ~30s, latest snapshot only), the
// controls and the IBKR account.
const Automation = () => {
  const { message } = App.useApp();
  const [overview, setOverview] = useState(null);
  // the bot answers over its socket: null = not asked yet, false = no answer
  const [gateway, setGateway] = useState(null);
  const [ibAccount, setIbAccount] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await apiGetAutomationOverview());
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadBot = useCallback(async () => {
    try {
      setGateway({ reachable: true, running: !!(await apiGetTwsStatus()) });
    } catch (err) {
      setGateway({ reachable: false, running: false });
      setIbAccount(null);
      return;
    }
    try {
      setIbAccount(await apiGetIBAccountDetails());
    } catch (err) {
      setIbAccount(null);
    }
  }, []);

  const refresh = useCallback(() => {
    loadOverview();
    loadBot();
  }, [loadOverview, loadBot]);

  useEffect(() => {
    document.title = "Automation | Dindicator Dashboard";
    refresh();
    const timers = [
      setInterval(loadOverview, REFRESH_MS),
      setInterval(loadBot, BOT_REFRESH_MS),
    ];
    return () => timers.forEach(clearInterval);
  }, [refresh, loadOverview, loadBot]);

  const run = async (key, action, done) => {
    setBusy(key);
    try {
      await action();
      if (done) message.success(done);
    } catch (err) {
      message.error(err?.response?.data?.error || "Something went wrong");
    } finally {
      setBusy(null);
      // the gateway takes a moment to start / stop
      setTimeout(refresh, key === "automation" ? 0 : 3000);
    }
  };

  if (!overview) {
    return <Divider orientation="left">Automation</Divider>;
  }

  if (!overview.enabled) {
    return (
      <>
        <Divider orientation="left">Automation</Divider>
        <GenericCard title="Automation isn't enabled for your account">
          <p>
            AutoTrader places your strategies' orders at Interactive Brokers.
            Ask the admin to enable it for your account.
          </p>
        </GenericCard>
      </>
    );
  }

  // older AutoTrader versions send no stats and no heartbeat: no Health card,
  // no stuck-loop check for them
  const legacyBot = !overview.stats;
  const stats = overview.stats || {};
  // two signals: the container answers the dashboard (tws_status over its
  // socket) = AutoTrader is alive; its trading loop checking in (heartbeat) =
  // the loop runs. A frozen loop still answers tws_status from its own thread.
  const checked = gateway !== null;
  const online = !!gateway?.reachable;
  const loopStuck =
    online &&
    !legacyBot &&
    (overview.seenSecondsAgo == null ||
      overview.seenSecondsAgo > LOOP_SILENT_SECONDS);
  const ibcStatus = ibAccount?.ibcStatus;
  // stats of an older bot version (none) or a stopped bot (stale) don't count
  const statsFresh =
    !!overview.statsAt &&
    dayjs().diff(dayjs(overview.statsAt)) < STATS_FRESH_MS;
  const ibConnected = statsFresh ? stats.ibConnected : ibAccount?.connected;

  const steps = [
    {
      key: "telegram",
      title: "Link Telegram",
      done: overview.telegramLinked,
      help: "Fills, exits and alerts are sent to your Telegram.",
      action: <LinkTelegramAccount />,
    },
    {
      key: "program",
      title: "AutoTrader program",
      done: online && !loopStuck,
      help: loopStuck
        ? "AutoTrader answers, but its trading loop isn't running. Restart its container or contact support."
        : "Your AutoTrader runs on our server. If it doesn't respond, contact support.",
    },
    {
      key: "login",
      title: "IBKR login",
      done: ibcStatus === "configured",
      help: "AutoTrader logs in to IB Gateway with your IBKR username (live or paper).",
      action: (
        <UpdateIBKRDetails
          completed={ibcStatus === "configured"}
          fetchIbAccountDetails={loadBot}
        />
      ),
    },
    {
      key: "gateway",
      title: "IB Gateway running",
      done: gateway?.running || ibConnected === true,
      help: "Approve the login in the IBKR mobile app if 2FA asks for it.",
      action: (
        <Button
          type="primary"
          loading={busy === "gateway"}
          onClick={() => run("gateway", apiStartTws, "IB Gateway is starting")}
        >
          Start IB Gateway
        </Button>
      ),
    },
    {
      key: "automation",
      title: "Automation on",
      done: overview.running,
      help: "AutoTrader places and manages orders only while this is on.",
      action: (
        <Button
          type="primary"
          loading={busy === "automation"}
          onClick={() =>
            run("automation", apiStartAutomation, "Automation is on")
          }
        >
          Turn automation on
        </Button>
      ),
    },
  ];
  const current = steps.findIndex((step) => !step.done);
  const setupDone = current === -1;

  const status = !checked
    ? { color: "default", text: "Checking…" }
    : !online
      ? { color: "red", text: "Offline" }
      : !overview.running
        ? { color: "default", text: "Off" }
        : loopStuck
          ? { color: "red", text: "Stuck" }
          : ibConnected === false
            ? { color: "orange", text: "Not connected to IBKR" }
            : { color: "green", text: "Running" };

  return (
    <div className="automation-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <Divider orientation="left">Automation</Divider>
        </div>
        <Tag color={status.color} className="automation-status-tag">
          {status.text}
        </Tag>
        <Tooltip title="Refresh">
          <Button shape="circle" icon={<ReloadOutlined />} onClick={refresh} />
        </Tooltip>
      </div>

      <div className="cards-wrapper">
        <GenericCard
          className="table-card"
          title={setupDone ? "Setup complete" : "Setup"}
          isCardOpenByDefault={!setupDone}
          tooltipText="What a new automation user goes through, in order"
        >
          <Steps
            direction="vertical"
            size="small"
            current={setupDone ? steps.length : current}
            items={steps.map((step, index) => ({
              title: step.title,
              status: step.done
                ? "finish"
                : index === current
                  ? "process"
                  : "wait",
              icon: step.done ? <CheckCircleFilled /> : undefined,
              description: (
                <div className="automation-step">
                  <p>{step.help}</p>
                  {!step.done && index === current && step.action && (
                    <div className="pt-2">{step.action}</div>
                  )}
                </div>
              ),
            }))}
          />
        </GenericCard>

        {!legacyBot && (
          <GenericCard
            title="Health"
            tooltipText="Sent by AutoTrader every ~30s; only the latest snapshot is kept"
          >
            <div className="automation-tiles">
              <Tile
                label="AutoTrader"
                value={
                  !checked
                    ? "-"
                    : !online
                      ? "Offline"
                      : loopStuck
                        ? "Stuck"
                        : "Online"
                }
                tone={!checked ? "" : online && !loopStuck ? "green" : "red"}
                sub={`Loop last checked in ${ago(overview.lastSeen)}`}
              />
              <Tile
                label="IBKR connection"
                value={
                  ibConnected == null
                    ? "-"
                    : ibConnected
                      ? "Connected"
                      : "Disconnected"
                }
                tone={
                  ibConnected ? "green" : ibConnected === false ? "red" : ""
                }
                sub={stats.ibSince ? `since ${ago(stats.ibSince)}` : ""}
              />
              <Tile
                label="Loop time (5 min)"
                value={ms(stats.loopAvgMs)}
                sub={`max ${ms(stats.loopMaxMs)}`}
                tone={stats.loopAvgMs > 3000 ? "yellow" : ""}
              />
              <Tile label="Working orders" value={stats.workingOrders ?? "-"} />
              <Tile
                label="Open positions"
                value={
                  stats.stockPositions == null
                    ? "-"
                    : stats.stockPositions + (stats.optionPositions || 0)
                }
                sub={
                  stats.optionPositions
                    ? `${stats.stockPositions} stocks, ${stats.optionPositions} options`
                    : ""
                }
              />
              <Tile
                label="Quote lines"
                value={stats.quoteLines ?? "-"}
                sub="of ~100 at IBKR"
              />
            </div>
            {stats.lastError && (
              <div className="automation-last-error">
                <ClockCircleOutlined /> Last error {ago(stats.lastErrorAt)}:{" "}
                <span className="red">{stats.lastError}</span>
              </div>
            )}
            <div className="automation-footnote">
              {stats.version ? `Version ${stats.version}` : "No stats yet"}
              {stats.startedAt ? ` · started ${ago(stats.startedAt)}` : ""}
              {overview.statsAt ? ` · updated ${ago(overview.statsAt)}` : ""}
            </div>
          </GenericCard>
        )}

        <GenericCard title="Controls">
          {gateway && !online && (
            <div className="automation-control-help pb-2">
              AutoTrader isn't responding: IB Gateway, the IBKR login and logs
              can't be managed until it's back.
            </div>
          )}
          <div className="automation-controls">
            <div className="automation-control">
              <div>
                <div className="automation-control-title">Automation</div>
                <div className="automation-control-help">
                  Off: AutoTrader places nothing and disconnects from IBKR
                </div>
              </div>
              <Switch
                checked={overview.running}
                loading={busy === "automation"}
                onChange={(on) =>
                  run(
                    "automation",
                    on ? apiStartAutomation : apiStopAutomation,
                    on ? "Automation is on" : "Automation is off",
                  )
                }
              />
            </div>
            {online && (
              <div className="automation-control">
                <div>
                  <div className="automation-control-title">
                    IB Gateway{" "}
                    {gateway?.running ? (
                      <Tag color="green">Running</Tag>
                    ) : (
                      <Tag>Stopped</Tag>
                    )}
                  </div>
                  <div className="automation-control-help">
                    Restart it when IBKR is disconnected
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {gateway?.running ? (
                    <>
                      <Button
                        loading={busy === "restart"}
                        onClick={() =>
                          run(
                            "restart",
                            apiRestartTws,
                            "IB Gateway is restarting",
                          )
                        }
                      >
                        Restart
                      </Button>
                      <Popconfirm
                        title="Stop IB Gateway?"
                        description="AutoTrader can't trade until it runs again"
                        onConfirm={() =>
                          run("gateway", apiStopTws, "IB Gateway stopped")
                        }
                      >
                        <Button danger loading={busy === "gateway"}>
                          Stop
                        </Button>
                      </Popconfirm>
                    </>
                  ) : (
                    <Button
                      type="primary"
                      disabled={!online || ibcStatus !== "configured"}
                      loading={busy === "gateway"}
                      onClick={() =>
                        run("gateway", apiStartTws, "IB Gateway is starting")
                      }
                    >
                      Start
                    </Button>
                  )}
                </div>
              </div>
            )}
            {online && (
              <div className="automation-control">
                <div>
                  <div className="automation-control-title">IBKR login</div>
                  <div className="automation-control-help">
                    Username, password and live / paper account
                  </div>
                </div>
                <UpdateIBKRDetails
                  completed={ibcStatus === "configured"}
                  fetchIbAccountDetails={loadBot}
                />
              </div>
            )}
            {online && (
              <div className="automation-control">
                <div>
                  <div className="automation-control-title">Logs</div>
                  <div className="automation-control-help">
                    AutoTrader's own log, newest first
                  </div>
                </div>
                <AutomationLogsModal />
              </div>
            )}
          </div>
        </GenericCard>
      </div>

      {ibAccount?.connected && (
        <>
          <Divider orientation="left">IBKR account</Divider>
          <IBAccountSection
            embedded
            account={{
              ibAccountDetails: ibAccount,
              loading: false,
              refresh: loadBot,
            }}
          />
        </>
      )}
    </div>
  );
};

const Tile = ({ label, value, sub, tone }) => (
  <div className="automation-tile">
    <div className="automation-tile-label">{label}</div>
    <div className={`automation-tile-value ${tone || ""}`}>{value}</div>
    {sub ? <div className="automation-tile-sub">{sub}</div> : null}
  </div>
);

export default Automation;
